/**
 * <analytics-report> — the operator's traffic surface: what a chosen range
 * added up to for one event. Its subject resolves from `consoleEventId`
 * (report.state.ts), so the console's Analytics destination mounts it bare and
 * it reports that event, with no property threaded in from its host.
 *
 * ── THE SHAPE IS THE NUMBER, AND THE NUMBER OPENS ───────────────────────────
 * Every datum rides a visual mark — stat tiles carrying sparklines and meters,
 * a real tapering funnel whose notch IS the drop, channel bars whose bright
 * inner fill is the paid share, segmented part-to-whole bars — so the shape
 * answers before the digits are read. Every figure is an `<analytics-figure>`,
 * which wears a dotted rule and opens its sources where `drill/drill.state.ts`
 * can answer the node behind it and prints plain where it cannot — so the
 * affordance appears by DATA, out of the one resolver, rather than by a flag a
 * host sets or by a second rendering of this panel.
 *
 * This element owns the one subscription that turns a press into a stack of
 * overlays: a level is terminated by a pop to any shallower depth, so a crumb
 * three levels down closes exactly the levels above it in one act. A chain in
 * the URL opens on connect, which is what makes a drill a link somebody can
 * paste into a message.
 *
 * ── DATAVIZ CHOICES ─────────────────────────────────────────────────────────
 * · Trend: two area charts sharing one x-span, never one plot with two y-axes.
 * · Funnel: ONE hue for every band; the taper carries the ordinal progression
 *   and the magnitude, so a per-stage ramp would double-encode the width.
 * · The lost cohort's segments pin TONES, not series steps: the three refusals
 *   the system issued share one error run and the two ways a buyer simply left
 *   share one neutral run, so the bar answers "how much of this was refused"
 *   while its key answers "refused how".
 * · Channels are bars, not a five-column grid: bar LENGTH is visits, the
 *   bright inner fill is the paid share, so each channel is its own mini
 *   funnel — long-but-pale converts poorly, short-but-bright converts well.
 * · Two visual kinds in the stat row and no third: a sparkline where the
 *   figure has a history, a meter where it is a rate against its own ceiling.
 * · The paid campaigns are cards, not a row of the channel bars: they are a
 *   different subject on a different clock — each campaign's whole run rather
 *   than the chosen window — and each is NAMED by that run's own period, tensed,
 *   before its verdict is read and long before any figure is: whether a run is
 *   still taking the operator's money is what a cost card exists to answer.
 *
 * It is one flowing block and establishes no scroll region of its own — the
 * destination that mounts it is the scrollport (analytics.view.ts). It opens
 * on its own filter row: the first thing on the panel is the window that scopes
 * every figure beneath it, so nothing on this surface can be read as scoped by
 * a range that does not govern it. What is true of the event THIS minute — how
 * many buyers are on it right now — is a claim no range can answer, so it
 * rides the identity band above instead, as that band's own figure under its
 * own label. The rest of the shop is nobody's subject on this destination: an
 * operator standing on one event is accounting for that event.
 *
 * Every knob is a controller, and no value on this surface is ever typed. The
 * whole knob set is ONE line — the window, the channel chips holding every
 * channel the range names with any number of them lit at once, and how fresh
 * the answer is at the far end. The visit gap that scopes all of it is not a
 * knob and not a caption: it is a row of the provenance every figure opens.
 *
 * A recompute holds the answer already on screen and dims it rather than
 * blanking numbers being read. Only a first load, which has no previous answer
 * to hold, shimmers.
 *
 * Zero I/O: the knobs write state, and whichever reconciler is mounted
 * answers.
 */
import "@mhmo91/schmancy/chart";
import type {
	ChartPoint,
	ChartSelectEvent,
	FunnelStage,
	SchmancyFunnelActivateEvent,
	SchmancyRowActivateEvent,
	SchmancySegmentActivateEvent,
} from "@mhmo91/schmancy/chart";
import "@mhmo91/schmancy/form/fields/chips";
import "@mhmo91/schmancy/form/fields/range";
import "@mhmo91/schmancy/progress";
import "@mhmo91/schmancy/skeleton";
import "@mhmo91/schmancy/visually-hidden";
import { formatCurrency, formatNumber, minorUnitDigits } from "@mhmo91/rx";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import type { MessageKey, TextKey } from "@momo/i18n";
import { type AnalyticsReport, Buyer } from "@momo/types";
import { css, html, nothing, type TemplateResult } from "lit";
import { choose } from "lit/directives/choose.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import {
	filter,
	finalize,
	from,
	ignoreElements,
	map,
	merge,
	mergeMap,
	takeUntil,
	tap,
} from "rxjs";
import "./analytics-figure.view.ts";
import { LocalizeMixin } from "../i18n/localize.ts";
import type { DrillNode } from "./analytics-figure.view.ts";
import "./drill/drill.view.ts";
import {
	answerable,
	decodeChain,
	drillOpen$,
	drillPop$,
	phraseText,
	pushDrill,
	resolveDrill,
	truncateDrill,
} from "./drill/drill.state.ts";
import {
	type CampaignCostRow,
	type ClientDimension,
	calendarDay,
	DIRECT_CHANNEL,
	FIGURE_LOCALE,
	RAW_WINDOW_DAYS,
	type ReportRangePreset,
	reportKnobs,
	reportViewModel,
	requestReportRefresh,
	resolveRange,
	type TrendPoint,
} from "./report.state.ts";

// Orders carry no currency field; every money call site in the console pins
// EUR the same way (dashboard.view.ts's own CURRENCY).
const CURRENCY = "EUR";

/** The arrow a signed change wears — an unchanged figure says so rather than
 *  pointing nowhere. */
const DELTA_MARK: Readonly<Record<number, string>> = {
	[-1]: "↓",
	0: "±",
	1: "↑",
};

/** A sparkline's own breathing room: the area must not touch the tile's
 *  edges, and the chart primitive's default padding is sized for an axis this
 *  one does not draw. */
const SPARK_PADDING = { top: 6, right: 4, bottom: 6, left: 4 };
const DAY_MS = 86_400_000;

/** Every figure and every share this surface prints, in one style. The chart
 *  primitives fall back to a bare `String(v)` and a spaceless `19%`, which
 *  would put an ungrouped number inside a funnel's caption and a differently
 *  punctuated percentage inside a bar's key while the figures beside them kept
 *  this surface's own — so both cross the boundary as formatters rather than
 *  being left to a default. */
const formatFigure = (value: number): string =>
	formatNumber(value, FIGURE_LOCALE);
const formatShare = (fraction: number): string =>
	`${(fraction * 100).toFixed(0)} %`;

/** What an unstatable funnel prints — the em dash a KPI tile already prints
 *  for a rate with no denominator. It stands in for BOTH primitive formatters,
 *  whose remaining readers are the band's accessible name and the share beside
 *  the slotted figure: a "0" left in either says, in the one place a screen
 *  reader hears and the one place beside an em dash, exactly the measured zero
 *  the dash denies. */
const unknownFigure = (): string => "—";

/** Money in the AD ACCOUNT's own unit, which is not this shop's: `CURRENCY`
 *  above and `<analytics-figure>`'s own `money` unit both pin EUR and both
 *  divide by a hundred, and an ad account is free to bill in a currency whose
 *  minor unit carries three places or none. So a spend crosses to the element
 *  as an already-printed `display` string. An account that has named no
 *  currency yet prints the bare figure rather than asserting one. */
const formatMinorUnit = (
	minor: number,
	currency: string | null | undefined,
	locale: string,
): string =>
	currency == null
		? formatNumber(Math.round(minor), locale)
		: formatCurrency(minor / 10 ** minorUnitDigits(currency), currency, locale);

/** An instant as this surface prints it — hours and minutes, the precision a
 *  freshness caption is read at. It takes the READER'S locale, not the pinned
 *  `FIGURE_LOCALE`: 24-hour against AM/PM is a fact about the language the
 *  caption is written in, where a grouping separator is a convention about the
 *  number. Nullish prints nothing rather than an Invalid Date. */
/** A trend's points as the plot takes them — the day printed against the
 *  READER'S locale, for the same reason the clock above is: `Aug` and `Aug.`
 *  are a language's, where a grouping separator is the figure's. The chart
 *  primitive formats values and never x labels, and it does not animate, so
 *  rebuilding the array per render costs a diff of two short strings and
 *  keeps the axis and the `<schmancy-date-range>` above it naming one month
 *  one way. */
const trendPoints = (points: readonly TrendPoint[], locale: string) =>
	points.map((point) => ({
		value: point.value,
		data: point,
		label: new Date(`${point.date}T00:00:00.000Z`).toLocaleDateString(locale, {
			day: "numeric",
			month: "short",
			timeZone: "UTC",
		}),
	}));

const formatClock = (iso: string | null | undefined, locale: string): string =>
	iso == null
		? ""
		: new Date(iso).toLocaleTimeString(locale, {
				hour: "2-digit",
				minute: "2-digit",
			});

const CLIENT_VALUE_LABELS: Readonly<Record<string, TextKey>> = {
	ios: "admin.analytics.clientValues.ios",
	android: "admin.analytics.clientValues.android",
	macos: "admin.analytics.clientValues.macos",
	windows: "admin.analytics.clientValues.windows",
	linux: "admin.analytics.clientValues.linux",
	chromeos: "admin.analytics.clientValues.chromeos",
	safari: "admin.analytics.clientValues.safari",
	chrome: "admin.analytics.clientValues.chrome",
	firefox: "admin.analytics.clientValues.firefox",
	edge: "admin.analytics.clientValues.edge",
	samsung: "admin.analytics.clientValues.samsung",
	phone: "admin.analytics.clientValues.phone",
	tablet: "admin.analytics.clientValues.tablet",
	desktop: "admin.analytics.clientValues.desktop",
	xs: "admin.analytics.clientValues.xs",
	sm: "admin.analytics.clientValues.sm",
	md: "admin.analytics.clientValues.md",
	lg: "admin.analytics.clientValues.lg",
	xl: "admin.analytics.clientValues.xl",
	portrait: "admin.analytics.clientValues.portrait",
	landscape: "admin.analytics.clientValues.landscape",
	other: "admin.analytics.clientValues.other",
};

const CLIENT_DIMENSION_LABELS = {
	os: "admin.analytics.clientOs",
	browser: "admin.analytics.clientBrowser",
	device: "admin.analytics.clientDevice",
	viewport: "admin.analytics.clientViewport",
	orientation: "admin.analytics.clientOrientation",
} as const satisfies Readonly<Record<ClientDimension, MessageKey>>;

const CLIENT_SPEC_AXES = [
	{ dimension: "os", label: CLIENT_DIMENSION_LABELS.os },
	{ dimension: "browser", label: CLIENT_DIMENSION_LABELS.browser },
	{ dimension: "device", label: CLIENT_DIMENSION_LABELS.device },
	{ dimension: "viewport", label: CLIENT_DIMENSION_LABELS.viewport },
	{ dimension: "orientation", label: CLIENT_DIMENSION_LABELS.orientation },
] as const satisfies ReadonlyArray<{
	readonly dimension: ClientDimension;
	readonly label: MessageKey;
}>;

export type AnalyticsFilter =
	| { readonly dimension: "utmSource"; readonly value: string }
	| { readonly dimension: "os"; readonly value: Buyer.ClientProfile["os"] }
	| {
			readonly dimension: "browser";
			readonly value: Buyer.ClientProfile["browser"];
	  }
	| {
			readonly dimension: "device";
			readonly value: Buyer.ClientProfile["device"];
	  }
	| {
			readonly dimension: "viewport";
			readonly value: Buyer.ClientProfile["viewport"];
	  }
	| {
			readonly dimension: "orientation";
			readonly value: Buyer.ClientProfile["orientation"];
	  };

export type AnalyticsAction =
	| { readonly type: "filter"; readonly filter: AnalyticsFilter }
	| { readonly type: "drill"; readonly node: DrillNode };

/**
 * The report's one action boundary. Client filters are a partial conjunction:
 * choosing a value replaces the value in that dimension, while activating the
 * same value again removes that dimension. Drills retain the existing stack's
 * level-zero entry point and never become a second state owner.
 */
export function applyAnalyticsAction(action: AnalyticsAction): void {
	if (action.type === "drill") {
		drillOpen$.next({ node: action.node, from: 0 });
		return;
	}
	if (action.filter.dimension === "utmSource") {
		const current = [...reportKnobs.value.utmSources];
		const next = current.includes(action.filter.value)
			? current.filter((source) => source !== action.filter.value)
			: [...current, action.filter.value];
		reportKnobs.set({ utmSources: next });
		return;
	}

	const current = reportKnobs.value.client ?? {};
	const next: AnalyticsReport.ClientFilter = { ...current };
	switch (action.filter.dimension) {
		case "os":
			if (current.os === action.filter.value) delete next.os;
			else next.os = action.filter.value;
			break;
		case "browser":
			if (current.browser === action.filter.value) delete next.browser;
			else next.browser = action.filter.value;
			break;
		case "device":
			if (current.device === action.filter.value) delete next.device;
			else next.device = action.filter.value;
			break;
		case "viewport":
			if (current.viewport === action.filter.value) delete next.viewport;
			else next.viewport = action.filter.value;
			break;
		case "orientation":
			if (current.orientation === action.filter.value) delete next.orientation;
			else next.orientation = action.filter.value;
			break;
	}
	reportKnobs.set({ client: next });
}

function filterFromSegment(
	dimension: ClientDimension,
	value: string,
): AnalyticsFilter | null {
	switch (dimension) {
		case "os": {
			const parsed = Buyer.ClientOsSchema.safeParse(value);
			return parsed.success ? { dimension, value: parsed.data } : null;
		}
		case "browser": {
			const parsed = Buyer.ClientBrowserSchema.safeParse(value);
			return parsed.success ? { dimension, value: parsed.data } : null;
		}
		case "device": {
			const parsed = Buyer.ClientDeviceSchema.safeParse(value);
			return parsed.success ? { dimension, value: parsed.data } : null;
		}
		case "viewport": {
			const parsed = Buyer.ClientViewportSchema.safeParse(value);
			return parsed.success ? { dimension, value: parsed.data } : null;
		}
		case "orientation": {
			const parsed = Buyer.ClientOrientationSchema.safeParse(value);
			return parsed.success ? { dimension, value: parsed.data } : null;
		}
	}
}

/** What a pressed figure opens, per stat tile — the kind of answer it wants
 *  and the figure it is about. Held here rather than on the view model because
 *  it is a property of the affordance this surface offers, not of the
 *  arithmetic behind the number. */
const KPI_DRILL = {
	revenue: { kind: "parts", subject: "revenue" },
	visits: { kind: "parts", subject: "visits" },
	visitors: { kind: "parts", subject: "visitors" },
	conversion: { kind: "provenance", subject: "conversion" },
	returning: { kind: "parts", subject: "returning" },
	dwell: { kind: "provenance", subject: "dwell" },
} as const satisfies Readonly<
	Record<string, { kind: DrillNode["kind"]; subject: string }>
>;

/** The node a figure carries, or null where the resolver has no answer for it
 *  — a figure is pressable exactly when pressing it opens something, which is
 *  one rule rather than a second list of which figures are live. */
const drillNode = (
	kind: DrillNode["kind"],
	subject: string,
	scope: readonly string[] = [],
): DrillNode | null => {
	const node: DrillNode = { kind, subject, scope };
	return answerable(node) ? node : null;
};

/** The three named windows the picker offers beside its own calendar — not a
 *  chip row bolted on next to it. Picking a preset and picking a custom span
 *  are the same gesture on the same component, and the trigger renders the
 *  active preset's own label, so the current window is legible at rest. */
const RANGE_PRESETS = [
	{ preset: "today", labelKey: "admin.analytics.rangeToday" },
	{ preset: "7d", labelKey: "admin.analytics.range7d" },
	{ preset: "30d", labelKey: "admin.analytics.range30d" },
] as const satisfies ReadonlyArray<{
	readonly preset: ReportRangePreset;
	readonly labelKey: MessageKey;
}>;

@schmancyElement("analytics-report")
export class AnalyticsReportView extends LocalizeMixin(SchmancyElement) {
	// A custom element is inline by default, and this one is the whole body of
	// the destination that mounts it — an inline host would wrap its column in
	// an anonymous inline box that takes neither the full width nor the gaps.
	static styles = [
		css`
      :host {
        display: block;
      }
    `,
	];

	private clientLabel(key: string): string {
		const labelKey = CLIENT_VALUE_LABELS[key];
		return labelKey === undefined ? key : this.t(labelKey);
	}

	private openDrill(node: DrillNode | null): void {
		if (node !== null) applyAnalyticsAction({ type: "drill", node });
	}

	private openTrendPoint(
		subject: string,
		points: readonly TrendPoint[],
		event: ChartSelectEvent,
	): void {
		const point = points[event.detail.index];
		if (point === undefined) return;
		this.openDrill(drillNode("parts", subject, [`day:${point.date}`]));
	}

	private openFunnelMark = (event: SchmancyFunnelActivateEvent): void => {
		if (event.detail.kind === "stage") {
			this.openDrill(drillNode("parts", `funnel.${event.detail.id}`));
			return;
		}
		this.openDrill(
			drillNode("boundary", "visitor.timeline", [
				`funnel-drop:${event.detail.id}`,
			]),
		);
	};

	private openOutcomeMark = (event: SchmancySegmentActivateEvent): void => {
		const id = event.detail.id;
		this.openDrill(
			drillNode(
				id === "abandoned" ? "boundary" : "provenance",
				id === "abandoned" ? "visitor.timeline" : "refusal",
				id === "abandoned" ? [] : [`outcome:${id}`],
			),
		);
	};

	private openRetrievalMark = (event: SchmancySegmentActivateEvent): void => {
		this.openDrill(
			drillNode("provenance", "retrieval", [`outcome:${event.detail.id}`]),
		);
	};

	/** A cart figure under partial coverage: read over evidence that was not
	 *  read whole, so the number is approximate in both directions. Bound
	 *  once, so a primitive holding it is handed the same function across
	 *  renders rather than a fresh identity that would redraw it. */
	private approximateFigure = (value: number): string =>
		this.t("admin.analytics.figureApproximate", { value: formatFigure(value) });

	/** What `<schmancy-funnel>` prints for itself under partial coverage: the
	 *  band's accessible name, and the drop between two bands. A BAND is marked
	 *  and a DROP is not — a drop is one band's count less the next one's, two
	 *  independently-missed figures, so the difference is not approximately the
	 *  drop and can move either way by more than either count did. The two are
	 *  told apart by the point the primitive hands in: a band's point carries
	 *  the stage's own id, a drop's carries the pair of ids it spans. */
	private funnelFigure = (
		value: number,
		point: ChartPoint<FunnelStage>,
	): string =>
		point.id === point.data?.id
			? this.approximateFigure(value)
			: formatFigure(value);

	override connectedCallback(): void {
		super.connectedCallback();
		// ONE pipeline. A press feeds `drillOpen$`; each emission becomes one
		// `show()` passed nothing but the surface's accessible name — where the
		// panel goes, how it is sized, which layout tier it takes and what
		// dismisses it are the primitive's own answers. What this element owns
		// is WHEN a level opens and closes: a level is terminated by a pop to
		// any shallower depth, so a crumb three levels down closes exactly the
		// levels above it, in one act.
		merge(
			merge(
				drillOpen$,
				// A pasted chain opens level by level under the same stack rule a
				// press obeys, so no path into the chain has its own arithmetic.
				from(
					decodeChain(
						new URLSearchParams(location.search).get("drill") ?? "",
					).map((node, index) => ({ node, from: index })),
				),
			).pipe(
				// The stack rule, encoded once for every emitter: cut the chain to
				// the level the press came from BEFORE pushing. The pop runs
				// synchronously, so every deeper overlay unsubscribes and finalizes
				// here, ahead of the push, rather than a beat later on top of it.
				tap(({ from: level }) => drillPop$.next(level)),
				map(({ node }) => ({ node, depth: pushDrill(node) })),
				// The overlay is named by the panel it carries, so a screen reader
				// hears which figure just opened rather than that something did.
				mergeMap(({ node, depth }) =>
					show(
						() => html`<analytics-drill .depth=${depth}></analytics-drill>`,
						{
							ariaLabel: phraseText(
								resolveDrill(node)?.title ?? { text: node.subject },
								this,
							),
						},
					).pipe(
						takeUntil(drillPop$.pipe(filter((shallower) => shallower < depth))),
						// `close()` awaits its own animation, so a panel dismissed on
						// pointerdown finalizes long after the same gesture's click. It
						// may cut the chain to its own level only while the chain has
						// not outgrown that level — which the pop above guarantees.
						finalize(() => truncateDrill(depth - 1)),
					),
				),
			),
			drillPop$.pipe(
				tap((depth) => truncateDrill(depth)),
				ignoreElements(),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	/** What a run is CALLED, and the first thing its card says. The contract
	 *  carries no operator-typed label, so a run is named by WHEN IT RAN — and
	 *  tensed, because whether a run is still taking the operator's money is the
	 *  one question a cost card exists to answer and no figure on it can. The
	 *  verb also stops the line reading as a second range control beside the
	 *  panel's own Today/7d/30d chips: a verb is not a filter.
	 *
	 *  The minute appears only where the day is shared (`showsLaunchTime`, a
	 *  fact about the roster) — the shortest name that is still distinct is the
	 *  only one worth printing. Read twice, once rendered and once as the scope
	 *  chip's spoken name, which is what earns the name. */
	private runName(row: CampaignCostRow): string {
		if (row.launchedAt == null)
			return this.t("admin.analytics.costRunUnstamped");
		// The locale's own span, dash and time convention — never a hand-rolled
		// separator, which would print an en dash into a language that closes a
		// range some other way.
		const period = new Intl.DateTimeFormat(this.locale, {
			day: "numeric",
			month: "long",
			...(row.showsLaunchTime ? { hour: "numeric", minute: "2-digit" } : {}),
		});

		return row.stoppedAt == null
			? this.t("admin.analytics.costRunOpen", {
					span: period.format(new Date(row.launchedAt)),
				})
			: this.t("admin.analytics.costRunClosed", {
					span: period.formatRange(
						new Date(row.launchedAt),
						new Date(row.stoppedAt),
					),
				});
	}

	override render(): TemplateResult {
		const vm = reportViewModel.get();
		const today = calendarDay(new Date());
		// The dates the picker opens on: a preset is a real span, so its
		// calendar shows that span rather than an empty month.
		const activeRange = resolveRange(vm.range, Date.now());
		// The floor every path into the picker clamps to — its own presets as
		// much as a custom span — so an operator cannot ask for a range the raw
		// timeline can no longer answer. `min` alone gates the calendar only.
		const retentionFloor = calendarDay(
			new Date(Date.now() - (RAW_WINDOW_DAYS - 1) * DAY_MS),
		);

		return html`<div
      class="flex flex-col gap-8"
      @drill=${(event: CustomEvent<DrillNode>) =>
				// The report is level 0, so a press here opens from 0 — the same rule
				// a panel obeys with its own depth, and the reason a press on the
				// surface starts a new chain rather than a deeper level of the open
				// one.
				applyAnalyticsAction({ type: "drill", node: event.detail })}
    >
      <schmancy-visually-hidden role="status" aria-live="polite">
        ${
					vm.firstLoad || vm.recomputing
						? this.t("admin.analytics.busyAnnouncement")
						: this.t("admin.analytics.readyAnnouncement")
				}
      </schmancy-visually-hidden>

      <!-- The closed knob set of the action contract and nothing else, on
			     ONE line: the window, the channel chips where the answer names more
			     than one channel, and how fresh that answer is at the far end. -->
      <div class="flex flex-wrap items-center gap-x-6 gap-y-3">
        <!-- Range: ONE control. The three named windows are this picker's
				     OWN presets, offered beside its calendar, and its trigger
				     renders the active preset's label — so the current window is
				     legible at rest and its arrows shift that window by its own
				     length without opening anything. -->
        <schmancy-date-range
          .presets=${RANGE_PRESETS.map((p) => {
						const span = resolveRange(
							{ kind: "preset", preset: p.preset },
							Date.now(),
						);
						return {
							id: p.preset,
							label: this.t(p.labelKey),
							start: span.from < retentionFloor ? retentionFloor : span.from,
							end: span.to,
						};
					})}
          .preset=${vm.range.kind === "preset" ? vm.range.preset : null}
          placeholder=${this.t("admin.analytics.rangeCustom")}
          min=${retentionFloor}
          max=${today}
          .start=${activeRange.from}
          .end=${activeRange.to}
          @change=${(
						e: CustomEvent<{
							start: string | null;
							end: string | null;
							preset: string | null;
						}>,
					) => {
						// The picker reports which of its own presets landed the
						// range, so a preset never has to be reverse-matched from
						// the two dates it happens to resolve to today.
						const picked = RANGE_PRESETS.find(
							(p) => p.preset === e.detail.preset,
						);
						if (picked) {
							if (
								vm.range.kind === "preset" &&
								vm.range.preset === picked.preset
							)
								return;
							reportKnobs.set({
								range: { kind: "preset", preset: picked.preset },
							});
							return;
						}
						if (e.detail.start === null || e.detail.end === null) return;
						if (
							vm.range.kind === "custom" &&
							vm.range.from === e.detail.start &&
							vm.range.to === e.detail.end
						)
							return;
						reportKnobs.set({
							range: {
								kind: "custom",
								from: e.detail.start,
								to: e.detail.end,
							},
						});
					}}
        ></schmancy-date-range>

        <!-- The chips host is width: fit-content, so it hugs its row and
				     overflows its track unless the call site constrains it. The
				     14rem basis is what makes them pack as a peer of the window on
				     this line, so the line WRAPS whole; a bare flex-1 resolves
				     against a zero basis and squeezes the scroller to a slot. -->
        <!-- The roster rides the answer rather than its channel rows, which a
				     scoped answer has already narrowed — so the row holds every
				     channel at every selection instead of collapsing to the one just
				     picked. Nothing lit is the whole storefront: the empty selection
				     is the state a standing "all" chip used to spell out. -->
        ${when(
					vm.campaignSources.length > 0,
					() => html`
            <schmancy-chips
              multi
              class="flex-1 basis-56"
              .value=${[...vm.utmSources]}
              @change=${(e: CustomEvent<{ value: string | string[] }>) => {
								const next = Array.isArray(e.detail.value)
									? e.detail.value
									: e.detail.value === ""
										? []
										: [e.detail.value];
								if (
									next.length === vm.utmSources.length &&
									next.every((source, i) => source === vm.utmSources[i])
								)
									return;
								reportKnobs.set({ utmSources: next });
							}}
            >
              ${repeat(
								vm.campaignSources,
								(source) => source,
								(source) =>
									html`<schmancy-filter-chip .value=${source}
                    >${
											source === DIRECT_CHANNEL
												? this.t("admin.analytics.channelDirect")
												: source
										}</schmancy-filter-chip
                  >`,
							)}
            </schmancy-chips>
          `,
				)}

        <!-- The campaign scope, on the one line the whole knob set is on.
				     Picking one happens on its own cost card at the foot of the
				     panel — this surface has no name for a campaign, so the card
				     an operator is reading is the only thing that identifies one —
				     which would leave every figure above reading as the whole
				     event's unless the scope stated itself up here, with the way
				     out on it. -->
        ${when(
					vm.campaignPicked,
					() => html`
            <schmancy-input-chip
              remove-label=${this.t("admin.analytics.campaignScopeClear")}
              @remove=${() => reportKnobs.set({ utmCampaigns: [] })}
            >
              ${this.t("admin.analytics.campaignScope")}
            </schmancy-input-chip>
          `,
				)}

        <!-- Freshness as a signal, not a sentence. The ml-auto is what
				     right-pins the stamp on the answers that name no channel. -->
        <div class="ml-auto flex items-center gap-2">
          ${when(
						vm.firstLoad || vm.recomputing || vm.computedAt === null,
						() => html`
              <schmancy-tone-dot
                class="h-2.5 w-2.5"
                color="warning"
                label=${this.t("admin.analytics.computing")}
              ></schmancy-tone-dot>
              <schmancy-typography
                type="label"
                token="xs"
                class="text-surface-onVariant"
              >
                ${this.t("admin.analytics.computing")}
              </schmancy-typography>
            `,
						() => html`
              <schmancy-tone-dot
                class="h-2.5 w-2.5"
                color=${vm.servedFromCache ? "neutral" : "success"}
                label=${this.t(
									vm.servedFromCache
										? "admin.analytics.asOfCached"
										: "admin.analytics.asOfFresh",
									{ time: formatClock(vm.computedAt, this.locale) },
								)}
              ></schmancy-tone-dot>
              <schmancy-typography
                type="label"
                token="xs"
                class="text-surface-onVariant tabular-nums"
              >
                ${this.t(
									vm.servedFromCache
										? "admin.analytics.asOfCached"
										: "admin.analytics.asOfFresh",
									{ time: formatClock(vm.computedAt, this.locale) },
								)}
              </schmancy-typography>
            `,
					)}
          <schmancy-icon-button
            size="sm"
            variant="text"
            .icon=${"refresh"}
            .busy=${vm.recomputing}
            ?disabled=${vm.firstLoad || vm.recomputing}
            title=${this.t("admin.analytics.refresh")}
            aria-label=${this.t("admin.analytics.refresh")}
            @click=${() => requestReportRefresh()}
          ></schmancy-icon-button>
        </div>
      </div>

      ${when(
				vm.failed,
				() => html`
          <schmancy-typography
            type="body"
            token="sm"
            class="text-error-default"
          >
            ${this.t("admin.analytics.reportFailed")}
          </schmancy-typography>
        `,
			)}
      ${when(
				vm.activeClientFilters.length > 0,
				() => html`
          <div
            class="flex flex-wrap items-center gap-2"
            aria-label=${this.t("admin.analytics.clientFiltersTitle")}
          >
            <schmancy-typography
              type="label"
              token="sm"
              class="text-surface-onVariant"
            >
              ${this.t("admin.analytics.clientFiltersTitle")}
            </schmancy-typography>
            ${repeat(
							vm.activeClientFilters,
							(active) => `${active.dimension}:${active.value}`,
							(active) => {
								const label = `${this.t(CLIENT_DIMENSION_LABELS[active.dimension])}: ${this.clientLabel(active.value)}`;
								return html`
                  <schmancy-input-chip
                    remove-label=${this.t("admin.analytics.clientFilterClear", { filter: label })}
                    @remove=${() => {
											const filter = filterFromSegment(
												active.dimension,
												active.value,
											);
											if (filter)
												applyAnalyticsAction({ type: "filter", filter });
										}}
                  >
                    ${label}
                  </schmancy-input-chip>
                `;
							},
						)}
          </div>
        `,
			)}

      <div
        aria-busy=${vm.recomputing ? "true" : "false"}
        class=${this.classMap({
					"flex flex-col gap-8 transition-opacity duration-200": true,
					"opacity-45": vm.recomputing,
				})}
      >
        <!-- ── STAT ROW: one reconciled row ──────────────────────────────
				     One card per figure, not one grid of bare pairs: a tile carries
				     its label, its figure with the change beside it on one baseline,
				     and the course that figure took under it. -->
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          ${repeat(
						vm.kpis,
						(tile) => tile.key,
						(tile) => html`
              <schmancy-surface
                type="containerLow"
                class="flex flex-col gap-1 rounded-xl p-4"
              >
                <schmancy-typography
                  type="label"
                  token="sm"
                  class="text-surface-onVariant"
                >
                  ${this.t(tile.labelKey)}
                </schmancy-typography>
                ${when(
									vm.firstLoad,
									() => html`
                    <schmancy-skeleton
                      shape="text"
                      class="w-16"
                    ></schmancy-skeleton>
                    <schmancy-skeleton
                      shape="text"
                      class="mt-2 h-6 w-full"
                    ></schmancy-skeleton>
                  `,
									() => html`
                    <div class="flex items-baseline justify-between gap-2">
                      <analytics-figure
                        size="stat"
                        .label=${this.t(tile.labelKey)}
                        .value=${tile.value}
                        .unit=${tile.unit}
                        .display=${tile.display}
                        .node=${drillNode(
													KPI_DRILL[tile.key].kind,
													KPI_DRILL[tile.key].subject,
												)}
                      ></analytics-figure>
                      ${when(
												tile.deltaPercent !== null,
												() => html`
                          <schmancy-typography
                            type="label"
                            token="sm"
                            title=${this.t("admin.analytics.deltaWithinRange")}
                            class=${this.classMap({
															"tabular-nums": true,
															"text-surface-onVariant": tile.deltaPercent === 0,
															"text-success-default":
																(tile.deltaPercent ?? 0) > 0,
															"text-error-default":
																(tile.deltaPercent ?? 0) < 0,
														})}
                          >
                            ${`${DELTA_MARK[Math.sign(tile.deltaPercent ?? 0)]} ${Math.abs(tile.deltaPercent ?? 0)} %`}
                          </schmancy-typography>
                        `,
											)}
                    </div>
                    ${choose(tile.visual?.kind ?? "none", [
											[
												"series",
												// A single primary-hue area, no axis, no gridlines, no
												// per-point labels: the figure above it carries the value.
												() => html`
                          <schmancy-area-chart
                            class="block w-full text-primary-default"
                            .data=${(
															tile.visual?.kind === "series"
																? tile.visual.points
																: []
														).map((value) => ({ value }))}
                            height=${36}
                            .padding=${SPARK_PADDING}
                            interactive
                            tooltip
                            @select=${() =>
															this.openDrill(
																drillNode(
																	KPI_DRILL[tile.key].kind,
																	KPI_DRILL[tile.key].subject,
																),
															)}
                          ></schmancy-area-chart>
                        `,
											],
											[
												"meter",
												() => html`
                          <schmancy-progress
                            class="mt-2 block"
                            size="sm"
                            color="primary"
                            value=${tile.visual?.kind === "meter" ? tile.visual.percent : 0}
                            max="100"
                          ></schmancy-progress>
                        `,
											],
											[
												"none",
												// A tile with no visual leaves this row empty, and one
												// tile has a reason for its dash worth a line. It takes
												// the geometry the meter would have — the same block,
												// the same top margin, one line clipped rather than
												// wrapped — so the six-tile grid row never grows a line
												// on the day the reason appears. Read at rest: a
												// hover-only reveal is nothing at all on touch.
												() =>
													tile.noteKey === null
														? nothing
														: html`
                          <schmancy-typography
                            type="label"
                            token="xs"
                            class="mt-2 block truncate text-warning-default"
                          >
                            ${this.t(tile.noteKey)}
                          </schmancy-typography>
                        `,
											],
										])}
                  `,
								)}
              </schmancy-surface>
            `,
					)}
        </div>

        <!-- ── TREND: two plots, one x span, never two y-axes ────────────
				     Visits and money share nothing but their days, and one plot with
				     two scales invents a correlation the data does not carry. Absent
				     on a one-day range, because one point is not a trend. -->
        ${when(
					vm.showTrend,
					() => html`
            <div class="grid gap-6 lg:grid-cols-2">
              <div class="flex flex-col gap-2">
                <schmancy-typography
                  type="label"
                  token="sm"
                  class="text-surface-onVariant"
                >
                  ${this.t("admin.analytics.trendVisits")}
                </schmancy-typography>
                <schmancy-area-chart
                  class="block w-full text-primary-default"
                  .data=${trendPoints(vm.visitsTrend, this.locale)}
                  height=${150}
                  peaks=${1}
                  tooltip
                  interactive
                  @select=${(event: ChartSelectEvent) =>
										this.openTrendPoint("visits", vm.visitsTrend, event)}
                  xLabels="auto"
                ></schmancy-area-chart>
              </div>
              <div class="flex flex-col gap-2">
                <schmancy-typography
                  type="label"
                  token="sm"
                  class="text-surface-onVariant"
                >
                  ${this.t("admin.analytics.trendRevenue")}
                </schmancy-typography>
                <schmancy-area-chart
                  class="block w-full text-primary-default"
                  .data=${trendPoints(vm.revenueTrend, this.locale)}
                  .valueFormatter=${(v: number) =>
										formatCurrency(v, CURRENCY, FIGURE_LOCALE)}
                  height=${150}
                  peaks=${1}
                  tooltip
                  interactive
                  @select=${(event: ChartSelectEvent) =>
										this.openTrendPoint("revenue", vm.revenueTrend, event)}
                  xLabels="auto"
                ></schmancy-area-chart>
              </div>
            </div>
          `,
				)}
        ${when(
					vm.hasAnswer,
					() => html`
            <div class="flex flex-col gap-3" data-client-specs>
              <schmancy-typography type="title" token="md">
                ${this.t("admin.analytics.clientSpecsTitle")}
              </schmancy-typography>
              ${when(
								vm.clientSpecsEmpty,
								() => html`
                  <schmancy-typography
                    type="body"
                    token="sm"
                    class="text-surface-onVariant"
                  >
                    ${this.t("admin.analytics.clientSpecsEmpty")}
                  </schmancy-typography>
                `,
							)}
              ${when(
								vm.clientSpecsEmpty || vm.coverage.clientSpecs !== "complete",
								() => html`
                  <schmancy-typography
                    type="body"
                    token="sm"
                    class=${this.classMap({
											"text-surface-onVariant":
												vm.coverage.clientSpecs !== "unavailable",
											"text-warning-default":
												vm.coverage.clientSpecs === "unavailable",
										})}
                  >
                    ${
											vm.coverage.clientSpecs === "complete"
												? this.t("admin.analytics.clientCoverageComplete")
												: vm.coverage.clientSpecs === "partial"
													? this.t("admin.analytics.clientCoveragePartial")
													: this.t("admin.analytics.clientCoverageUnavailable")
										}
                  </schmancy-typography>
                `,
							)}
              ${when(
								!vm.clientSpecsEmpty,
								() => html`
                  <schmancy-surface
                    type="containerLow"
                    class="grid gap-5 rounded-xl p-4 md:grid-cols-2 md:p-5"
                  >
                    ${CLIENT_SPEC_AXES.filter(
											({ dimension }) => vm.clientSpecs[dimension].length > 0,
										).map(({ dimension, label }) => {
											return html`
                        <div
                          class="flex flex-col gap-2"
                          data-client-dimension=${dimension}
                        >
                          <schmancy-typography
                            type="label"
                            token="sm"
                            class="text-surface-onVariant"
                          >
                            ${this.t(label)}
                          </schmancy-typography>
                          <schmancy-pills
                            selectable
                            .showLegend=${false}
                            thickness="20"
                            .selectedId=${vm.clientFilters[dimension] ?? null}
                            .data=${vm.clientSpecs[dimension].map((row) => ({
															id: row.key,
															label: this.clientLabel(row.key),
															value: row.count,
														}))}
                            .valueFormatter=${formatFigure}
                            @row-activate=${(
															event: SchmancyRowActivateEvent,
														) => {
															const activated = filterFromSegment(
																dimension,
																String(event.detail.id),
															);
															if (activated)
																applyAnalyticsAction({
																	type: "filter",
																	filter: activated,
																});
														}}
                          ></schmancy-pills>
                        </div>
                      `;
										})}
                  </schmancy-surface>
                `,
							)}
            </div>
          `,
				)}

        <!-- ── FUNNEL: the drop is the shape between two bands ───────────
				     The primitive paints one continuous silhouette whose taper IS
				     the cohort that left, and the word for that cohort is named once
				     as the wedge's legend rather than once per gap. Every stage's
				     figure rides the primitive's own figure-<id> slot, which is
				     what makes a number printed inside a shadow root pressable at
				     all where a surface supplies drill nodes. -->
        <div class="flex flex-col gap-3">
          <schmancy-typography type="title" token="md">
            ${this.t("admin.analytics.funnelTitle")}
          </schmancy-typography>
          <!-- Two independent facts, so two independent lines rather than one
					     sentence that would have to say both: how much of the range was
					     counted at all, and how whole the evidence was on the days that
					     were. A period can carry either, both, or neither. -->
          ${when(
						vm.funnelCoverage.span !== "whole",
						() => html`
              <schmancy-typography
                type="body"
                token="sm"
                class="text-warning-default"
              >
                ${
									vm.funnelCoverage.span === "none"
										? this.t("admin.analytics.funnelCoverageUnavailable")
										: this.t("admin.analytics.funnelCoverageCountedDays", {
												counted: vm.funnelCoverage.countedDays,
												total: vm.funnelCoverage.rangeDays,
											})
								}
              </schmancy-typography>
            `,
					)}
          ${when(
						vm.funnelCoverage.reading === "partial",
						() => html`
              <schmancy-typography
                type="body"
                token="sm"
                class="text-surface-onVariant"
              >
                ${this.t("admin.analytics.funnelCoveragePartial")}
              </schmancy-typography>
            `,
					)}
          <schmancy-surface
            type="containerLow"
            class="flex flex-col gap-4 rounded-xl p-4 md:p-5"
          >
            <schmancy-funnel
              color="primary"
              loss="neutral"
              interactive
              @funnel-activate=${this.openFunnelMark}
              bandHeight="44"
              dropHeight="14"
              dropHeightSpan="46"
              .stages=${vm.funnel.map((row) => ({
								id: row.key,
								label: this.t(row.labelKey),
								// The silhouette is a figure too, so it asserts nothing the
								// counts do not: handed no denominator, the primitive paints
								// its own neutral ladder instead of a confident taper over
								// four em dashes. Under partial it draws the counts as read
								// — the taper is band over first band, one arm on both
								// sides, which is the one ratio short evidence leaves exact.
								value: vm.funnelCoverage.span === "none" ? 0 : row.value,
							}))}
              .lossLabel=${this.t("admin.analytics.droppedOut")}
              .valueFormatter=${
								vm.funnelCoverage.span === "none"
									? unknownFigure
									: vm.funnelCoverage.reading === "partial"
										? this.funnelFigure
										: formatFigure
							}
              .shareFormatter=${
								vm.funnelCoverage.span === "none" ? unknownFigure : formatShare
							}
            >
              ${repeat(
								vm.funnel,
								(row) => row.key,
								(row) => html`
                  <analytics-figure
                    slot=${`figure-${row.key}`}
                    size="inline"
                    .label=${this.t(row.labelKey)}
                    .value=${row.value}
                    .display=${row.display}
                    ?approximate=${vm.funnelCoverage.reading === "partial"}
                    unit="count"
                    .node=${drillNode("parts", `funnel.${row.key}`)}
                  ></analytics-figure>
                `,
							)}

              <!-- A refusal and an abandonment are not stages: they leave
							     the funnel sideways, which is why they hang off it as its
							     own breakdown rather than as a fifth band. The operator's
							     question is proportion — how much of the loss the system
							     refused versus how much simply walked away — so the three
							     refusals share the error tone and the two departures share
							     neutral: the bar answers how much, and its key, which the
							     primitive renders itself, answers which. -->
              ${when(
								vm.outcomes.length > 0,
								() => html`
                  <div slot="loss-breakdown" class="flex flex-col gap-2">
                    <schmancy-segmented-bar
                      thickness="14"
                      interactive
                      @segment-activate=${this.openOutcomeMark}
                      .segments=${vm.outcomes.map((outcome) => ({
												id: outcome.key,
												label: this.t(outcome.labelKey),
												value: outcome.count,
												color: outcome.tone,
											}))}
                      .valueFormatter=${
												vm.funnelCoverage.reading === "partial"
													? this.approximateFigure
													: formatFigure
											}
                      .shareFormatter=${formatShare}
                    >
                      <schmancy-typography
                        slot="header"
                        type="label"
                        token="sm"
                      >
                        ${this.t("admin.analytics.outcomesTitle")}
                      </schmancy-typography>
                      ${repeat(
												vm.outcomes,
												(outcome) => outcome.key,
												(outcome) => html`
                          <analytics-figure
                            slot=${`figure-${outcome.key}`}
                            size="inline"
                            .label=${this.t(outcome.labelKey)}
                            .value=${outcome.count}
                            ?approximate=${vm.funnelCoverage.reading === "partial"}
                            unit="count"
                            .node=${
															outcome.key === "abandoned"
																? drillNode("boundary", "visitor.timeline")
																: drillNode("provenance", "refusal", [
																		`outcome:${outcome.key}`,
																	])
														}
                          ></analytics-figure>
                        `,
											)}
                    </schmancy-segmented-bar>
                  </div>
                `,
							)}
            </schmancy-funnel>
          </schmancy-surface>
        </div>

        <!-- ── CHANNELS: a bar comparison, not a column grid ─────────────
				     Bar LENGTH is visits (comparison across channels); the bright
				     inner fill is the paid share, so each channel is its own mini
				     funnel — long-but-pale converts poorly, short-but-bright
				     converts well. The direct row carries no fill and no rate: a
				     visit that named no campaign carries no key to the basket it
				     later started, and subtracting the named rows from the funnel's
				     paid count would silently mix two populations into a rate. -->
        <div class="flex flex-col gap-3">
          <schmancy-typography type="title" token="md">
            ${this.t("admin.analytics.campaignsTitle")}
          </schmancy-typography>
          ${when(
						vm.campaigns.length === 0,
						() => html`
              <schmancy-typography
                type="body"
                token="sm"
                class="text-surface-onVariant"
              >
                ${this.t("admin.analytics.campaignsEmpty")}
              </schmancy-typography>
            `,
						() => html`
              <schmancy-surface
                type="containerLow"
                class="flex flex-col gap-3 rounded-xl p-4 md:p-5"
              >
                ${repeat(
									vm.campaigns,
									(row) => row.source,
									(row) => {
										const label =
											row.source === DIRECT_CHANNEL
												? this.t("admin.analytics.channelDirect")
												: row.source;
										return html`
                      <div
                        class="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 md:grid-cols-[10.5rem_minmax(0,1fr)_auto]"
                      >
                        <schmancy-typography
                          type="body"
                          token="sm"
                          class="truncate"
                        >
                          ${label}
                        </schmancy-typography>
                        <button
                          type="button"
                          class="order-3 col-span-2 block h-5 appearance-none overflow-hidden rounded-md border-0 bg-transparent p-0 text-left focus:outline-solid focus:outline-2 focus:outline-offset-0 focus:outline-secondary-default md:order-none md:col-span-1"
                          style="width: ${(row.visits / vm.campaignMax) * 100}%"
                          data-channel-source=${row.source}
                          aria-label=${`${label}: ${formatFigure(row.visits)}`}
                          title=${`${label}: ${formatFigure(row.visits)}`}
                          @click=${() =>
														row.source === DIRECT_CHANNEL
															? this.openDrill(
																	drillNode("parts", "channel", [
																		`channel:${row.source}`,
																	]),
																)
															: applyAnalyticsAction({
																	type: "filter",
																	filter: {
																		dimension: "utmSource",
																		value: row.source,
																	},
																})}
                        >
                          <div class="h-full rounded-md bg-primary-default/25">
                            <div
                              class="h-full rounded-md bg-primary-default"
                              style="width: ${
																row.paid === null || row.visits === 0
																	? 0
																	: (row.paid / row.visits) * 100
															}%"
                            ></div>
                          </div>
                        </button>
                        <div class="flex items-baseline gap-3">
                          <analytics-figure
                            size="inline"
                            .label=${label}
                            .value=${row.visits}
                            unit="count"
                            .node=${drillNode("parts", "channel", [
															`channel:${row.source}`,
														])}
                          ></analytics-figure>
                          <schmancy-typography
                            type="label"
                            token="xs"
                            class="w-12 text-right text-surface-onVariant tabular-nums"
                          >
                            ${
															row.paid === null || row.visits === 0
																? "—"
																: `${((row.paid / row.visits) * 100).toFixed(1)} %`
														}
                          </schmancy-typography>
                        </div>
                      </div>
                    `;
									},
								)}
                <div class="flex flex-wrap items-center gap-x-4 gap-y-1 pt-1">
                  <div class="flex items-center gap-1.5">
                    <div
                      class="h-2.5 w-4 rounded-sm bg-primary-default/25"
                    ></div>
                    <schmancy-typography
                      type="label"
                      token="xs"
                      class="text-surface-onVariant"
                    >
                      ${this.t("admin.analytics.colVisits")}
                    </schmancy-typography>
                  </div>
                  <div class="flex items-center gap-1.5">
                    <div class="h-2.5 w-4 rounded-sm bg-primary-default"></div>
                    <schmancy-typography
                      type="label"
                      token="xs"
                      class="text-surface-onVariant"
                    >
                      ${this.t("admin.analytics.legendPaidShare")}
                    </schmancy-typography>
                  </div>
                </div>
              </schmancy-surface>
            `,
					)}
        </div>

        <!-- ── WHAT THE ADVERTISING COST ─────────────────────────────────
				     Meta's figures beside this shop's own, never summed: the ad
				     network is the sole source for the spend and no source at all
				     for the sales. Revenue is absent because it counts euro cents
				     the spend beside it may not, so their ratio is one nobody
				     could stand behind.

				     The name leads, then the verdict, then the digits. The
				     contract carries no operator-typed label, so a run is called
				     by when it ran — and a card that cannot say whether it is
				     STILL spending renders a run stopped weeks ago exactly like
				     four still charging. The verdict follows because three of the
				     four leave every figure under them at zero and only one of
				     the three is a fault, so digits alone cannot separate a
				     campaign nobody clicked from one whose clicks never arrived. -->
        ${when(
					vm.campaignCosts.length > 0,
					() => html`
            <div class="flex flex-col gap-3">
              <schmancy-typography type="title" token="md">
                ${this.t("admin.analytics.costTitle")}
              </schmancy-typography>
              <schmancy-typography
                type="body"
                token="sm"
                class="max-w-prose text-surface-onVariant text-pretty"
              >
                ${this.t("admin.analytics.costSubtitle")}
              </schmancy-typography>
              ${repeat(
								vm.campaignCosts,
								(row) => row.campaignId,
								(row) => html`
                  <schmancy-surface
                    type="containerLow"
                    class="flex flex-col gap-4 rounded-xl p-4 md:p-5"
                  >
                    <div class="flex items-start gap-3">
                      <div class="flex flex-col gap-1">
                        <!-- A thing is NAMED before it is diagnosed: the run's
												     own period takes the title, and the verdict yields
												     the top line without being demoted below the digits
												     — it still stands above the money row. -->
                        <schmancy-typography type="title" token="sm">
                          ${this.runName(row)}
                        </schmancy-typography>
                        <!-- The dot rides the verdict it is the mark for. It
												     carries no label of its own: the words beside it
												     name the same state, and a labelled mark would make
												     every card announce its verdict twice. -->
                        <span class="flex items-center gap-2">
                          <schmancy-tone-dot
                            class="h-2.5 w-2.5 shrink-0"
                            color=${row.trafficTone}
                          ></schmancy-tone-dot>
                          <schmancy-typography
                            type="label"
                            token="md"
                            class=${this.classMap({
															"text-error-default": row.trafficTone === "error",
														})}
                          >
                            ${this.t(row.trafficLabelKey)}
                          </schmancy-typography>
                        </span>
                        <schmancy-typography
                          type="body"
                          token="sm"
                          class=${this.classMap({
														"max-w-prose text-pretty": true,
														"text-error-default": row.trafficTone === "error",
														"text-surface-onVariant":
															row.trafficTone !== "error",
													})}
                        >
                          ${this.t(row.trafficWhyKey)}
                        </schmancy-typography>
                      </div>
                      <!-- Which run the whole panel above reports on. One at a
											     time: two picked runs summed into one figure is a
											     figure the operator cannot attribute to either.
											     Achromatic on purpose — the card spends its one
											     chromatic mark on the verdict.

											     The visible label is identical on every card because
											     the ACTION is identical; what differs is the run it
											     acts on, which rides after it unseen. Seven chips
											     spoken alike are seven controls nobody can address by
											     voice, by screen reader, or by a harness — and the
											     spoken name still opens with the visible words, so
											     Label in Name holds. -->
                      <schmancy-filter-chip
                        class="ml-auto"
                        color="surface"
                        variant="solid"
                        .value=${row.metaCampaignId}
                        ?selected=${row.picked}
                        @change=${(
													e: CustomEvent<{ value: string; selected: boolean }>,
												) =>
													reportKnobs.set({
														utmCampaigns: e.detail.selected
															? [e.detail.value]
															: [],
													})}
                      >
                        ${this.t("admin.analytics.costScopeOnly")}
                        <schmancy-visually-hidden>
                          ${this.runName(row)}
                        </schmancy-visually-hidden>
                      </schmancy-filter-chip>
                    </div>

                    <div class="flex flex-wrap items-start gap-x-8 gap-y-3">
                      <analytics-figure
                        size="stat"
                        showLabel
                        unit="money"
                        .label=${this.t("admin.analytics.costSpent")}
                        .value=${row.spendMinor}
                        .display=${formatMinorUnit(
													row.spendMinor,
													row.currency,
													this.locale,
												)}
                      ></analytics-figure>
                      <!-- The division the contract refuses to carry, done where
											     the answer can also be a sentence: nothing sold yet is
											     not an infinite cost, and it is not a zero either. -->
                      <analytics-figure
                        size="stat"
                        showLabel
                        unit="money"
                        .label=${this.t("admin.analytics.costPerTicket")}
                        .value=${row.costPerTicketMinor ?? 0}
                        .display=${
													row.costPerTicketMinor === null
														? this.t("admin.analytics.costPerTicketNone")
														: formatMinorUnit(
																row.costPerTicketMinor,
																row.currency,
																this.locale,
															)
												}
                      ></analytics-figure>
                      <analytics-figure
                        size="stat"
                        showLabel
                        unit="count"
                        .label=${this.t("admin.analytics.costTicketsSold")}
                        .value=${row.observed?.ticketsSold ?? 0}
                      ></analytics-figure>
                    </div>

                    <!-- The two halves, each stamped with when IT was read: a
										     throttled ad account leaves Meta's line hours behind
										     this shop's own while both sit on one card. -->
                    <div class="flex flex-col gap-0.5">
                      ${when(
												row.attributed != null,
												() => html`
                          <schmancy-typography
                            type="body"
                            token="xs"
                            class="text-surface-onVariant tabular-nums"
                          >
                            ${this.t("admin.analytics.costMetaClicks", {
															clicks: formatFigure(row.attributed?.clicks ?? 0),
															time: formatClock(
																row.attributed?.readAt,
																this.locale,
															),
														})}
                          </schmancy-typography>
                        `,
											)}
                      ${when(
												row.observed != null,
												() => html`
                          <schmancy-typography
                            type="body"
                            token="xs"
                            class="text-surface-onVariant tabular-nums"
                          >
                            ${this.t("admin.analytics.costObservedVisits", {
															visitors: formatFigure(
																row.observed?.visitors ?? 0,
															),
															orders: formatFigure(row.observed?.orders ?? 0),
															time: formatClock(
																row.observed?.readAt,
																this.locale,
															),
														})}
                          </schmancy-typography>
                        `,
											)}
                      <!-- A figure is never quoted without the terms it was read
											     under, and the campaign card states them in these very
											     words — one window, one sentence, both surfaces. -->
                      ${when(
												row.window != null,
												() => html`
                          <schmancy-typography
                            type="body"
                            token="xs"
                            class="text-surface-onVariant"
                          >
                            ${this.t("admin.campaign.status.window", {
															window: row.window ?? "",
														})}
                          </schmancy-typography>
                        `,
											)}
                    </div>
                  </schmancy-surface>
                `,
							)}
            </div>
          `,
				)}

        <!-- ── RETRIEVAL: one segmented part-to-whole bar ────────────────
				     The operator's question is which share of the collected tickets
				     each channel carries, which is one read across a track and five
				     reads down a column. A SINGLE channel is not a part-to-whole —
				     one full segment carries no proportion and would render as an
				     unnamed slab — so it reads as the one fact it is instead. -->
        <div class="flex flex-col gap-3">
          <schmancy-typography type="title" token="md">
            ${this.t("admin.analytics.retrievalTitle")}
          </schmancy-typography>
          <schmancy-typography
            type="body"
            token="sm"
            class="text-surface-onVariant"
          >
            ${this.t("admin.analytics.retrievalSubtitle")}
          </schmancy-typography>
          ${when(
						vm.retrieval.length === 0,
						() => html`
              <schmancy-typography
                type="body"
                token="sm"
                class="text-surface-onVariant"
              >
                ${this.t("admin.analytics.retrievalEmpty")}
              </schmancy-typography>
            `,
						() => html`
              <schmancy-surface
                type="containerLow"
                class="flex flex-col gap-3 rounded-xl p-4 md:p-5"
              >
                ${when(
									vm.retrieval.length === 1,
									() => html`
                    <div class="flex items-baseline gap-3">
                      <analytics-figure
                        size="stat"
                        .label=${this.t(
													vm.retrieval[0]?.labelKey ??
														"admin.analytics.retrievalTitle",
												)}
                        .value=${vm.retrieval[0]?.value ?? 0}
                        unit="count"
                        .node=${drillNode("provenance", "retrieval", [
													`outcome:${vm.retrieval[0]?.key ?? ""}`,
												])}
                      ></analytics-figure>
                      <schmancy-typography
                        type="body"
                        token="sm"
                        class="text-surface-onVariant"
                      >
                        ${this.t("admin.analytics.retrievalAllVia", {
													channel: this.t(
														vm.retrieval[0]?.labelKey ??
															"admin.analytics.retrievalTitle",
													),
												})}
                      </schmancy-typography>
                    </div>
                  `,
									() => html`
                    <schmancy-segmented-bar
                      thickness="14"
                      interactive
                      @segment-activate=${this.openRetrievalMark}
                      .segments=${vm.retrieval.map((row) => ({
												id: row.key,
												label: this.t(row.labelKey),
												value: row.value,
											}))}
                      .valueFormatter=${formatFigure}
                      .shareFormatter=${formatShare}
                    >
                      <schmancy-typography
                        slot="header"
                        type="label"
                        token="sm"
                        class="text-surface-onVariant"
                      >
                        ${this.t("admin.analytics.retrievalCollections")}
                      </schmancy-typography>
                      ${repeat(
												vm.retrieval,
												(row) => row.key,
												(row) => html`
                          <analytics-figure
                            slot=${`figure-${row.key}`}
                            size="inline"
                            .label=${this.t(row.labelKey)}
                            .value=${row.value}
                            unit="count"
                            .node=${drillNode("provenance", "retrieval", [
															`outcome:${row.key}`,
														])}
                          ></analytics-figure>
                        `,
											)}
                    </schmancy-segmented-bar>
                  `,
								)}
              </schmancy-surface>
            `,
					)}
        </div>
      </div>

      <!-- Said once, quietly, where it qualifies everything above it. -->
      <schmancy-typography
        type="body"
        token="xs"
        class="max-w-prose text-surface-onVariant"
      >
        ${this.t("admin.analytics.browserBias")}
      </schmancy-typography>
    </div>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"analytics-report": AnalyticsReportView;
	}
}
